import { inject } from '@angular/core';
import { CanMatchFn, Route, Routes } from '@angular/router';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { switchMap, take } from 'rxjs/operators';
import { DemoModeService } from '../services/demo-mode.service';
import { MembershipService } from '../services/membership.service';
import { PublicRefusalRouteComponent } from '../pages/public-refusal/public-refusal-route.component';
import { RouteData } from './route-data';

/**
 * The refusal face's ONE seat is the route table: the member-only top-level nodes sit under a pathless parent
 * this match gates, and a twin derived from the same nodes renders the face at every member path, at any depth,
 * behind the inverse gate. For a public-mode caller the member-only routes therefore do not EXIST — no member
 * host is constructed, no member guard runs, no member chunk downloads, and the URL stays — while a member, and
 * every caller of a brand whose public access is off, meets the table as it stands today.
 *
 * The match reads the authentication state the way every shipped guard does and AWAITS the membership pair for
 * an authenticated caller: public mode is false until the membership settles, so an early answer would send a
 * signed-in non-member into the member area on a cold load. A membership read that FAILED leaves public mode
 * false and the member area matching — the shipped guards own that transient (a signed-in non-member meets
 * today's bounce there), a named acceptance: the alternative would paint the public face over a possible member.
 * Demo mode matches at once, as every shipped guard admits it.
 */
export const memberAreaMatch: CanMatchFn = () => {
  if (inject(DemoModeService).isDemo()) return true;
  const oidc = inject(OidcSecurityService);
  const membership = inject(MembershipService);
  return oidc.isAuthenticated$.pipe(
    take(1),
    switchMap(async ({ isAuthenticated }) => {
      if (isAuthenticated) await membership.ensureLoaded();
      return !membership.publicMode();
    }),
  );
};

/**
 * The twin's gate: the face is reachable in public mode ALONE. Ungated, a member's nested-unknown member URL would
 * meet the public face instead of the app's own wildcard.
 */
export const publicModeOnlyMatch: CanMatchFn = () => inject(MembershipService).publicMode();

/**
 * The refusal twin, DERIVED from the member array: one node per member path, rendering the face's routed host at
 * that path and at any depth below it — never reading a node's own loader or guards, so no member chunk downloads.
 */
export function toRefusalRoutes(nodes: Routes): Routes {
  return nodes.map(node => ({
    path: node.path,
    children: [faceNode(''), faceNode('**')],
  }));
}

function faceNode(path: string): Route {
  return { path, component: PublicRefusalRouteComponent, data: { publicRefusal: true } satisfies RouteData };
}
